import React, { useState, useEffect } from 'react';
import { MoreVertical, Users, Pencil, Trash2, ChevronDown } from 'lucide-react';
import {
  fetchUserGroups,
  createGroup,
  fetchGroupTasks,
  addGroupTask,
  acceptGroupTask,
  listAcceptedTasks,
  updateGroupTask,
  deleteGroupTask,
  listGroupMembers,
  inviteMemberToGroup,
  removeMemberFromGroup,
  renameGroup,
  deleteGroup,
} from '../services/groupService';
import FloatingComposer from './FloatingComposer';
import './SharedTabView.css';


function SharedTabView({ selectedDate }) {
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupData, setGroupData] = useState({});
  const [acceptedTasks, setAcceptedTasks] = useState([]);
  const [inviteInput, setInviteInput] = useState('');
  const [panelTab, setPanelTab] = useState('tasks'); // 'tasks' | 'members'
  const [menuFor, setMenuFor] = useState(null);      // which group's ⋮ menu is open
  const [editingGroupId, setEditingGroupId] = useState(null); // inline rename
  const [editName, setEditName] = useState('');
  // Members (user IDs) a new task is assigned to via the composer "+" panel.
  // Empty = open to the whole group.
  const [shareWith, setShareWith] = useState([]);

  // Stable per-username avatar colour (members have no photo, so we show an
  // initial in a coloured circle).
  const AVATAR_COLORS = ['#FF6B6B', '#0D9488', '#3B82F6', '#22C55E', '#6366F1', '#EC4899', '#F59E0B'];
  const avatarColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
  };

  // Close the group ⋮ menu on any outside click.
  useEffect(() => {
    const onClick = () => setMenuFor(null);
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Normalise members to { id, username:<string>, is_admin } regardless of the
  // backend shape (string[], {username}[], or nested objects).
  const normalizeMembers = (gd) => {
    const raw = (gd?.memberDetails && gd.memberDetails.length) ? gd.memberDetails : (gd?.members || []);
    return raw.map((m, i) => {
      if (typeof m === 'string') return { id: `u${i}`, username: m, is_admin: false };
      const uname = typeof m.username === 'string'
        ? m.username
        : (m.username && (m.username.username || m.username.name)) || m.name || (m.id != null ? `User ${m.id}` : `Member ${i + 1}`);
      return { id: m.id != null ? m.id : `u${i}`, username: String(uname), is_admin: !!m.is_admin };
    });
  };

  const startRename = (g) => {
    setMenuFor(null);
    setEditingGroupId(g.id);
    setEditName(g.name);
  };
  const saveGroupName = async (g) => {
    const name = editName.trim();
    setEditingGroupId(null);
    if (!name || name === g.name) return;
    try { await renameGroup(g.id, name); await loadGroups(); }
    catch (_) { window.alert('Could not rename — only the group admin can rename it.'); }
  };

  const deleteGroupFlow = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? This removes it for everyone.`)) return;
    try {
      await deleteGroup(g.id);
      if (expandedGroup === g.id) setExpandedGroup(null);
      await loadGroups();
    } catch (_) { window.alert('Could not delete — only the group admin can delete it.'); }
  };

  useEffect(() => {
    loadGroups();
    loadAcceptedTasks();
  }, []);

  const loadGroups = async () => {
    try {
      const data = await fetchUserGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Failed to load groups:', err);
    }
  };

  const loadAcceptedTasks = async () => {
    try {
      const data = await listAcceptedTasks();
      setAcceptedTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Failed to load accepted tasks:', err);
    }
  };

  const toggleGroupExpand = async (groupId) => {
    setShareWith([]); // reset task assignment when switching / collapsing folders
    setPanelTab('tasks');
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(groupId);
      try {
        const [tasks, members] = await Promise.all([
          fetchGroupTasks(groupId),
          listGroupMembers(groupId),
        ]);
        setGroupData(prev => ({
          ...prev,
          [groupId]: {
            tasks: tasks || [],
            members: members.members || [],
            memberDetails: members.member_details || [],
            newTask: '',
            editTaskId: null,
            editText: '',
          }
        }));
      } catch (err) {
        console.error("❌ Failed to load group data", err);
      }
    }
  };

  const handleCreateGroup = async () => {
    const trimmed = newGroup.trim();
    if (!trimmed) return;
    try {
      await createGroup(trimmed);
      setNewGroup('');
      await loadGroups();
    } catch (err) {
      console.error('❌ Failed to create group:', err);
    }
  };

  const handleInputChange = (groupId, field, value) => {
    setGroupData(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [field]: value
      }
    }));
  };

  // Add a task to the open group from the bottom composer, then refresh the
  // group's task list in place (without collapsing it).
  const addTaskFromComposer = async (text) => {
    const gid = expandedGroup;
    if (!gid) return;
    await addGroupTask(gid, text, shareWith);
    setShareWith([]);
    try {
      const [tasks, members] = await Promise.all([
        fetchGroupTasks(gid),
        listGroupMembers(gid),
      ]);
      setGroupData((prev) => ({
        ...prev,
        [gid]: {
          ...(prev[gid] || {}),
          tasks: tasks || [],
          members: members.members || [],
          memberDetails: members.member_details || [],
          newTask: '',
        },
      }));
    } catch (err) {
      console.error('❌ Failed to refresh group', err);
    }
  };

  const handleEditSave = async (groupId, taskId) => {
    const newText = groupData[groupId].editText.trim();
    if (!newText) return;
    try {
      await updateGroupTask(taskId, newText);
      await toggleGroupExpand(groupId);
    } catch (err) {
      console.error('❌ Failed to update task', err);
    }
  };

  const handleDeleteTask = async (groupId, taskId) => {
    try {
      await deleteGroupTask(taskId);
      await toggleGroupExpand(groupId);
    } catch (err) {
      console.error('❌ Failed to delete task', err);
    }
  };

  const handleAcceptTask = async (taskId) => {
    try {
      await acceptGroupTask(taskId);
      await loadAcceptedTasks();
    } catch (err) {
      console.error('❌ Failed to accept shared task', err);
    }
  };

  const handleInvite = async (groupId, username) => {
    try {
      await inviteMemberToGroup(groupId, username);
      await toggleGroupExpand(groupId);
      setInviteInput('');
    } catch (err) {
      console.error('❌ Failed to invite', err);
    }
  };

  const handleRemoveMember = async (groupId, memberId) => {
    try {
      await removeMemberFromGroup(groupId, memberId);
      await toggleGroupExpand(groupId);
    } catch (err) {
      console.error('❌ Failed to remove', err);
    }
  };

  return (
    <div className="shared-container">
      {/* Create group */}
      <div className="section-header-premium">
        <div>
          <p className="subtitle">Collaborate with family &amp; friends</p>
        </div>
        <div className="new-group-wrapper">
          <input
            type="text"
            placeholder="New group name..."
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); }}
            className="new-group-input"
          />
          <button onClick={handleCreateGroup} className="create-group-btn" aria-label="Create group">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Expandable group cards — click a card to open it; everything (tasks,
          members, invite) lives inside, and the ⋮ menu handles edit/delete. */}
      <div className="group-cards">
        {groups.length === 0 && <p className="no-groups-msg">Create a group to start sharing tasks!</p>}
        {groups.map((g) => {
          const isOpen = expandedGroup === g.id;
          const gd = groupData[g.id];
          const members = isOpen && gd ? normalizeMembers(gd) : [];
          return (
            <div key={g.id} className={`group-card${isOpen ? ' open' : ''}`}>
              {/* Header — click toggles expand/collapse */}
              <div className="group-card-head" onClick={() => toggleGroupExpand(g.id)}>
                <div className="folder-icon-wrapper">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div className="group-info">
                  {editingGroupId === g.id ? (
                    <input
                      className="group-name-input"
                      value={editName}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') saveGroupName(g);
                        if (e.key === 'Escape') setEditingGroupId(null);
                      }}
                      onBlur={() => saveGroupName(g)}
                    />
                  ) : (
                    <span className="group-name">{g.name}</span>
                  )}
                  <span className="member-count">
                    {isOpen ? (gd ? `${members.length} member${members.length !== 1 ? 's' : ''}` : 'Loading…') : 'Tap to view'}
                  </span>
                </div>

                {/* ⋮ menu — edit / members / delete */}
                <div className="group-menu-wrap" onClick={(e) => e.stopPropagation()}>
                  <button className="group-menu-btn" onClick={() => setMenuFor(menuFor === g.id ? null : g.id)} aria-label="Group options" title="Options">
                    <MoreVertical size={18} />
                  </button>
                  {menuFor === g.id && (
                    <div className="group-menu">
                      <button onClick={() => { if (!isOpen) toggleGroupExpand(g.id); setPanelTab('members'); setMenuFor(null); }}>
                        <Users size={15} /> Members
                      </button>
                      <button onClick={() => startRename(g)}>
                        <Pencil size={15} /> Edit name
                      </button>
                      <button className="danger" onClick={() => { setMenuFor(null); deleteGroupFlow(g); }}>
                        <Trash2 size={15} /> Delete group
                      </button>
                    </div>
                  )}
                </div>

                <ChevronDown size={18} className={`group-chevron${isOpen ? ' open' : ''}`} />
              </div>

              {/* Body — expanded content */}
              {isOpen && (gd ? (
                <div className="group-card-body">
                  <div className="panel-tabs">
                    <button className={`panel-tab${panelTab === 'tasks' ? ' active' : ''}`} onClick={() => setPanelTab('tasks')}>Tasks</button>
                    <button className={`panel-tab${panelTab === 'members' ? ' active' : ''}`} onClick={() => setPanelTab('members')}>Members ({members.length})</button>
                  </div>

                  {panelTab === 'tasks' ? (
                    <div className="shared-tasks-list">
                      {gd.tasks.length === 0 && <p className="empty-state">No shared tasks yet.</p>}
                      {gd.tasks.map((task) => (
                        <div key={task.id} className="shared-task-card">
                          <div className="task-left">
                            <div className="task-avatar-placeholder">
                              {task.created_by.username.charAt(0).toUpperCase()}
                            </div>
                            <div className="task-content">
                              <span className="task-text-shared">{task.text}</span>
                              <span className="task-meta">Added by {task.created_by.username}</span>
                            </div>
                          </div>
                          <div className="task-actions">
                            <button onClick={() => handleAcceptTask(task.id)} className="accept-task-btn" title="Accept Task">Accept</button>
                            <button onClick={() => handleDeleteTask(g.id, task.id)} className="delete-task-icon" aria-label="Delete task">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="members-view">
                      <div className="member-avatars">
                        {members.slice(0, 8).map((m) => (
                          <span key={m.id} className="member-avatar" style={{ background: avatarColor(m.username) }} title={m.username}>
                            {(m.username || '?').charAt(0).toUpperCase()}
                          </span>
                        ))}
                        {members.length > 8 && <span className="member-avatar more">+{members.length - 8}</span>}
                      </div>
                      <ul className="member-list">
                        {members.map((m) => (
                          <li key={m.id} className="member-row">
                            <span className="member-avatar sm" style={{ background: avatarColor(m.username) }}>
                              {(m.username || '?').charAt(0).toUpperCase()}
                            </span>
                            <div className="member-info">
                              <span className="member-name">{m.username}</span>
                              {m.is_admin && <span className="member-admin">Admin</span>}
                            </div>
                            {!m.is_admin && typeof m.id === 'number' && (
                              <button className="member-remove" onClick={() => handleRemoveMember(g.id, m.id)} title="Remove member" aria-label="Remove member">×</button>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div className="invite-section-mini">
                        <input
                          type="text"
                          placeholder="Invite username..."
                          value={inviteInput}
                          onChange={(e) => setInviteInput(e.target.value)}
                          className="invite-input"
                        />
                        <button onClick={() => handleInvite(g.id, inviteInput)} className="invite-btn-mini">Invite</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="group-card-body"><div className="loading-state">Loading group data…</div></div>
              ))}
            </div>
          );
        })}
      </div>

      {/* The composer is tied to the open group — only shows while one is open. */}
      {expandedGroup && (
        <FloatingComposer
          placeholder={`Add task to ${groups.find((g) => g.id === expandedGroup)?.name || 'group'}…`}
          onSubmit={addTaskFromComposer}
          optionsPanel={renderShareOptions()}
        />
      )}
    </div>
  );

  // Composer "+" panel for Team: assign the new task to specific members
  // (shared_with). No selection = open to everyone in the group.
  function renderShareOptions() {
    const members = (expandedGroup && groupData[expandedGroup]?.memberDetails) || [];
    if (members.length === 0) return null;
    const toggle = (id) => setShareWith((ids) => (
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    ));
    return (
      <>
        <span className="fc-opt-label">Assign to</span>
        <div className="fc-opt-row">
          {members.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`fc-chip${shareWith.includes(m.id) ? ' active' : ''}`}
              onClick={() => toggle(m.id)}
            >
              {m.username}
            </button>
          ))}
        </div>
        <span className="fc-opt-note">
          {shareWith.length === 0 ? 'Open to everyone in the group' : `Assigned to ${shareWith.length} member${shareWith.length > 1 ? 's' : ''}`}
        </span>
      </>
    );
  }
}

export default SharedTabView;
